import type { Container, ContainerFilters, ContainerStats } from "@/types/container";
import { generateRealisticRoute } from "@/utils/route-generator";
import { groupContainersByWeek, generateYearlyWeekData, getCurrentYear, getWeekRange, type WeekData } from "@/utils/week-utils";
import { shipperQuoteService } from "./mock-quotes";
import { orderService } from "./mock-orders";
import { accountService } from "./mock-accounts";

// Mock data per containers
const mockContainers: Container[] = [
    {
        id: "1",
        shipperId: "1", // TechCorp Solutions LLC
        receiverId: "2", // Global Partners Corp
        containerNumber: "TCLU1234567",
        route: { from: "New York, USA", to: "Porto di Genova, Italia" },
        progress: 75,
        eta: new Date("2024-02-15"),
        documentStatus: "Completo",
        riskLevel: "Low",
        value: { amount: 125000, currency: "USD" },
        status: "In Transit",
        shippingCompany: "MSC",
        commodity: "electronics",
        createdAt: new Date("2024-01-20"),
        updatedAt: new Date("2024-02-01"),
        waypoints: generateRealisticRoute("New York, USA", "Porto di Genova, Italia", new Date("2024-01-20")),
        isConfirmed: true,
        bookingNumber: "FF-BK-2024-001",
    },
    {
        id: "2",
        shipperId: "1", // TechCorp Solutions LLC
        receiverId: "4", // Green Future NGO
        containerNumber: "TCLU1234568",
        route: { from: "Lima, Peru", to: "Rotterdam, Netherlands" },
        progress: 45,
        eta: new Date("2024-02-20"),
        documentStatus: "Pending",
        riskLevel: "Medium",
        value: { amount: 85000, currency: "USD" },
        status: "In Transit",
        shippingCompany: "Maersk",
        commodity: "avocado",
        createdAt: new Date("2024-01-25"),
        updatedAt: new Date("2024-02-01"),
        waypoints: generateRealisticRoute("Lima, Peru", "Rotterdam, Netherlands", new Date("2024-01-25")),
        isConfirmed: false,
    },
    {
        id: "3",
        shipperId: "3", // Innovation Hub Partnership
        receiverId: "6", // European Trading Co.
        containerNumber: "IHDE1234567",
        route: { from: "Nairobi, Kenya", to: "Porto di Livorno, Italia" },
        progress: 100,
        eta: new Date("2024-01-30"),
        documentStatus: "Completo",
        riskLevel: "Low",
        value: { amount: 95000, currency: "EUR" },
        status: "Delivered",
        shippingCompany: "CMA CGM",
        commodity: "coffee",
        createdAt: new Date("2024-01-10"),
        updatedAt: new Date("2024-01-30"),
        waypoints: generateRealisticRoute("Nairobi, Kenya", "Porto di Livorno, Italia", new Date("2024-01-10")),
        isConfirmed: true,
        bookingNumber: "FF-BK-2024-002",
    },
    {
        id: "4",
        shipperId: "7", // Sole Proprietor Consulting
        receiverId: "8", // Health Foundation NGO
        containerNumber: "SPCA1234567",
        route: { from: "Santiago, Chile", to: "Barcellona, Spagna" },
        progress: 30,
        eta: new Date("2024-02-25"),
        documentStatus: "Error",
        riskLevel: "High",
        value: { amount: 45000, currency: "CAD" },
        status: "Delayed",
        shippingCompany: "Hapag Lloyd",
        commodity: "blueberries",
        createdAt: new Date("2024-01-28"),
        updatedAt: new Date("2024-02-01"),
        waypoints: generateRealisticRoute("Santiago, Chile", "Barcellona, Spagna", new Date("2024-01-28")),
        isConfirmed: false,
    },
    {
        id: "5",
        shipperId: "1", // TechCorp Solutions LLC
        receiverId: "2", // Global Partners Corp
        containerNumber: "TCLU1234569",
        route: { from: "Guayaquil, Ecuador", to: "Civitavecchia, Italia" },
        progress: 90,
        eta: new Date("2024-02-10"),
        documentStatus: "Completo",
        riskLevel: "Low",
        value: { amount: 200000, currency: "USD" },
        status: "At Port",
        shippingCompany: "MSC",
        commodity: "banana",
        createdAt: new Date("2024-01-15"),
        updatedAt: new Date("2024-02-01"),
        waypoints: generateRealisticRoute("Guayaquil, Ecuador", "Civitavecchia, Italia", new Date("2024-01-15")),
        isConfirmed: true,
        bookingNumber: "FF-BK-2024-003",
    },
    {
        id: "6",
        shipperId: "3", // Innovation Hub Partnership
        receiverId: "4", // Green Future NGO
        containerNumber: "IHDE1234568",
        route: { from: "Costa Rica, San José", to: "FOS, Francia" },
        progress: 60,
        eta: new Date("2024-02-18"),
        documentStatus: "Pending",
        riskLevel: "Medium",
        value: { amount: 75000, currency: "EUR" },
        status: "In Transit",
        shippingCompany: "ONE",
        commodity: "pineapple",
        createdAt: new Date("2024-01-22"),
        updatedAt: new Date("2024-02-01"),
        waypoints: generateRealisticRoute("Costa Rica, San José", "FOS, Francia", new Date("2024-01-22")),
        isConfirmed: true,
        bookingNumber: "FF-BK-2024-004",
    },
    {
        id: "7",
        shipperId: "7", // Sole Proprietor Consulting
        receiverId: "6", // European Trading Co.
        containerNumber: "SPCA1234568",
        route: { from: "Manila, Filippine", to: "Rotterdam, Paesi Bassi" },
        progress: 15,
        eta: new Date("2024-03-01"),
        documentStatus: "Pending",
        riskLevel: "Critical",
        value: { amount: 30000, currency: "CAD" },
        status: "In Transit",
        shippingCompany: "Maersk",
        commodity: "mango",
        createdAt: new Date("2024-01-30"),
        updatedAt: new Date("2024-02-01"),
        waypoints: generateRealisticRoute("Manila, Filippine", "Rotterdam, Paesi Bassi", new Date("2024-01-30")),
        isConfirmed: false,
    },
    {
        id: "8",
        shipperId: "1", // TechCorp Solutions LLC
        receiverId: "8", // Health Foundation NGO
        containerNumber: "TCLU1234570",
        route: { from: "Ghana, Accra", to: "Porto di Genova, Italia" },
        progress: 100,
        eta: new Date("2024-01-25"),
        documentStatus: "Completo",
        riskLevel: "Low",
        value: { amount: 150000, currency: "USD" },
        status: "Delivered",
        shippingCompany: "CMA CGM",
        commodity: "cocoa",
        createdAt: new Date("2024-01-05"),
        updatedAt: new Date("2024-01-25"),
        waypoints: generateRealisticRoute("Ghana, Accra", "Porto di Genova, Italia", new Date("2024-01-05")),
        isConfirmed: true,
        bookingNumber: "FF-BK-2024-005",
    },
    // Nuove rotte complesse
    {
        id: "9",
        shipperId: "1", // TechCorp Solutions LLC
        receiverId: "2", // Global Partners Corp
        containerNumber: "TCLU1234571",
        route: { from: "Brasile, Santos", to: "Barcellona, Spagna" },
        progress: 55,
        eta: new Date("2024-03-10"),
        documentStatus: "Pending",
        riskLevel: "Medium",
        value: { amount: 300000, currency: "USD" },
        status: "In Transit",
        shippingCompany: "MSC",
        commodity: "sugar",
        createdAt: new Date("2024-01-15"),
        updatedAt: new Date("2024-02-01"),
        waypoints: generateRealisticRoute("Brasile, Santos", "Barcellona, Spagna", new Date("2024-01-15")),
        isConfirmed: false,
    },
    {
        id: "10",
        shipperId: "3", // Innovation Hub Partnership
        receiverId: "6", // European Trading Co.
        containerNumber: "IHDE1234569",
        route: { from: "Bangladesh, Chittagong", to: "Porto di Livorno, Italia" },
        progress: 40,
        eta: new Date("2024-03-15"),
        documentStatus: "Pending",
        riskLevel: "Medium",
        value: { amount: 180000, currency: "USD" },
        status: "In Transit",
        shippingCompany: "Maersk",
        commodity: "textiles",
        createdAt: new Date("2024-01-20"),
        updatedAt: new Date("2024-02-01"),
        waypoints: generateRealisticRoute("Bangladesh, Chittagong", "Porto di Livorno, Italia", new Date("2024-01-20")),
        isConfirmed: true,
        bookingNumber: "FF-BK-2024-006",
    },
    {
        id: "11",
        shipperId: "7", // Sole Proprietor Consulting
        receiverId: "2", // Global Partners Corp
        containerNumber: "SPCA1234569",
        route: { from: "Germania, Amburgo", to: "Civitavecchia, Italia" },
        progress: 25,
        eta: new Date("2024-03-05"),
        documentStatus: "Completo",
        riskLevel: "Low",
        value: { amount: 95000, currency: "USD" },
        status: "In Transit",
        shippingCompany: "CMA CGM",
        commodity: "machinery",
        createdAt: new Date("2024-01-25"),
        updatedAt: new Date("2024-02-01"),
        waypoints: generateRealisticRoute("Germania, Amburgo", "Civitavecchia, Italia", new Date("2024-01-25")),
        isConfirmed: true,
        bookingNumber: "FF-BK-2024-007",
    },
    {
        id: "12",
        shipperId: "1", // TechCorp Solutions LLC
        receiverId: "4", // Green Future NGO
        containerNumber: "TCLU1234572",
        route: { from: "Cina, Shanghai", to: "FOS, Francia" },
        progress: 70,
        eta: new Date("2024-02-28"),
        documentStatus: "Completo",
        riskLevel: "Low",
        value: { amount: 220000, currency: "USD" },
        status: "In Transit",
        shippingCompany: "ONE",
        commodity: "electronics",
        createdAt: new Date("2024-01-10"),
        updatedAt: new Date("2024-02-01"),
        waypoints: generateRealisticRoute("Cina, Shanghai", "FOS, Francia", new Date("2024-01-10")),
        isConfirmed: true,
        bookingNumber: "FF-BK-2024-008",
    },
    // Container aggiuntivi per il calendario settimanale - distribuiti su diverse settimane del 2024
    {
        id: "13",
        shipperId: "1", // TechCorp Solutions LLC
        receiverId: "2", // Global Partners Corp
        containerNumber: "TCLU1234573",
        route: { from: "Vietnam, Ho Chi Minh", to: "Porto di Genova, Italia" },
        progress: 20,
        eta: new Date("2024-11-15"), // Week 46
        documentStatus: "Pending",
        riskLevel: "Medium",
        value: { amount: 180000, currency: "USD" },
        status: "In Transit",
        shippingCompany: "MSC",
        commodity: "textiles",
        createdAt: new Date("2024-10-15"),
        updatedAt: new Date("2024-11-01"),
        waypoints: generateRealisticRoute("Vietnam, Ho Chi Minh", "Porto di Genova, Italia", new Date("2024-10-15")),
        isConfirmed: false,
    },
    {
        id: "14",
        shipperId: "3", // Innovation Hub Partnership
        receiverId: "2", // Global Partners Corp
        containerNumber: "IHDE1234570",
        route: { from: "Colombia, Cartagena", to: "Barcellona, Spagna" },
        progress: 60,
        eta: new Date("2024-11-22"), // Week 47
        documentStatus: "Completo",
        riskLevel: "Low",
        value: { amount: 95000, currency: "EUR" },
        status: "In Transit",
        shippingCompany: "Maersk",
        commodity: "coffee",
        createdAt: new Date("2024-10-20"),
        updatedAt: new Date("2024-11-01"),
        waypoints: generateRealisticRoute("Colombia, Cartagena", "Barcellona, Spagna", new Date("2024-10-20")),
        isConfirmed: true,
    },
    {
        id: "15",
        shipperId: "1", // TechCorp Solutions LLC
        receiverId: "2", // Global Partners Corp
        containerNumber: "TCLU1234574",
        route: { from: "Thailandia, Bangkok", to: "Rotterdam, Paesi Bassi" },
        progress: 10,
        eta: new Date("2024-12-05"), // Week 49
        documentStatus: "Pending",
        riskLevel: "High",
        value: { amount: 250000, currency: "USD" },
        status: "In Transit",
        shippingCompany: "CMA CGM",
        commodity: "electronics",
        createdAt: new Date("2024-11-01"),
        updatedAt: new Date("2024-11-01"),
        waypoints: generateRealisticRoute("Thailandia, Bangkok", "Rotterdam, Paesi Bassi", new Date("2024-11-01")),
        isConfirmed: false,
        bookingNumber: "FF-BK-2024-010",
    },
    {
        id: "16",
        shipperId: "7", // Sole Proprietor Consulting
        receiverId: "2", // Global Partners Corp
        containerNumber: "SPCA1234570",
        route: { from: "India, Mumbai", to: "Porto di Livorno, Italia" },
        progress: 85,
        eta: new Date("2024-11-08"), // Week 45 (settimana corrente)
        documentStatus: "Completo",
        riskLevel: "Low",
        value: { amount: 120000, currency: "USD" },
        status: "At Port",
        shippingCompany: "ONE",
        commodity: "textiles",
        createdAt: new Date("2024-09-15"),
        updatedAt: new Date("2024-11-01"),
        waypoints: generateRealisticRoute("India, Mumbai", "Porto di Livorno, Italia", new Date("2024-09-15")),
        isConfirmed: true,
    },
    {
        id: "17",
        shipperId: "3", // Innovation Hub Partnership
        receiverId: "2", // Global Partners Corp
        containerNumber: "IHDE1234571",
        route: { from: "Brasile, Rio de Janeiro", to: "FOS, Francia" },
        progress: 40,
        eta: new Date("2024-12-20"), // Week 51
        documentStatus: "Pending",
        riskLevel: "Medium",
        value: { amount: 160000, currency: "EUR" },
        status: "In Transit",
        shippingCompany: "Hapag Lloyd",
        commodity: "sugar",
        createdAt: new Date("2024-11-01"),
        updatedAt: new Date("2024-11-01"),
        waypoints: generateRealisticRoute("Brasile, Rio de Janeiro", "FOS, Francia", new Date("2024-11-01")),
        isConfirmed: false,
    },
    {
        id: "18",
        shipperId: "1", // TechCorp Solutions LLC
        receiverId: "2", // Global Partners Corp
        containerNumber: "TCLU1234575",
        route: { from: "Messico, Veracruz", to: "Civitavecchia, Italia" },
        progress: 75,
        eta: new Date("2024-11-29"), // Week 48
        documentStatus: "Completo",
        riskLevel: "Low",
        value: { amount: 85000, currency: "USD" },
        status: "In Transit",
        shippingCompany: "MSC",
        commodity: "avocado",
        createdAt: new Date("2024-10-25"),
        updatedAt: new Date("2024-11-01"),
        waypoints: generateRealisticRoute("Messico, Veracruz", "Civitavecchia, Italia", new Date("2024-10-25")),
        isConfirmed: true,
    },
    // ------------------- 2025 -------------------
    {
        id: "19",
        shipperId: "1",
        receiverId: "2",
        containerNumber: "TCLU1250001",
        route: { from: "USA, Miami", to: "Porto di Genova, Italia" },
        progress: 35,
        eta: new Date("2025-01-12"), // week early Jan
        documentStatus: "Pending",
        riskLevel: "Medium",
        value: { amount: 110000, currency: "USD" },
        status: "In Transit",
        shippingCompany: "MSC",
        commodity: "banana",
        createdAt: new Date("2024-12-20"),
        updatedAt: new Date("2024-12-28"),
        waypoints: generateRealisticRoute("USA, Miami", "Porto di Genova, Italia", new Date("2024-12-20")),
        isConfirmed: false,
    },
    {
        id: "20",
        shipperId: "3",
        receiverId: "2",
        containerNumber: "IHDE1250002",
        route: { from: "Kenya, Mombasa", to: "Porto di Livorno, Italia" },
        progress: 55,
        eta: new Date("2025-03-18"),
        documentStatus: "Completo",
        riskLevel: "Low",
        value: { amount: 140000, currency: "USD" },
        status: "In Transit",
        shippingCompany: "CMA CGM",
        commodity: "coffee",
        createdAt: new Date("2025-02-20"),
        updatedAt: new Date("2025-03-01"),
        waypoints: generateRealisticRoute("Kenya, Mombasa", "Porto di Livorno, Italia", new Date("2025-02-20")),
        isConfirmed: true,
    },
    {
        id: "21",
        shipperId: "1",
        receiverId: "2",
        containerNumber: "TCLU1250003",
        route: { from: "Vietnam, Ho Chi Minh", to: "Rotterdam, Paesi Bassi" },
        progress: 20,
        eta: new Date("2025-06-06"),
        documentStatus: "Pending",
        riskLevel: "High",
        value: { amount: 220000, currency: "USD" },
        status: "In Transit",
        shippingCompany: "Maersk",
        commodity: "textiles",
        createdAt: new Date("2025-05-01"),
        updatedAt: new Date("2025-05-20"),
        waypoints: generateRealisticRoute("Vietnam, Ho Chi Minh", "Rotterdam, Paesi Bassi", new Date("2025-05-01")),
        isConfirmed: false,
    },
    {
        id: "22",
        shipperId: "7",
        receiverId: "2",
        containerNumber: "SPCA1250004",
        route: { from: "Perù, Callao", to: "Barcellona, Spagna" },
        progress: 75,
        eta: new Date("2025-09-15"),
        documentStatus: "Completo",
        riskLevel: "Low",
        value: { amount: 90000, currency: "USD" },
        status: "In Transit",
        shippingCompany: "ONE",
        commodity: "avocado",
        createdAt: new Date("2025-08-10"),
        updatedAt: new Date("2025-08-25"),
        waypoints: generateRealisticRoute("Perù, Callao", "Barcellona, Spagna", new Date("2025-08-10")),
        isConfirmed: true,
    },
    {
        id: "23",
        shipperId: "3",
        receiverId: "2",
        containerNumber: "IHDE1250005",
        route: { from: "India, Mundra", to: "FOS, Francia" },
        progress: 10,
        eta: new Date("2025-11-20"), // late year for week view
        documentStatus: "Pending",
        riskLevel: "Medium",
        value: { amount: 150000, currency: "USD" },
        status: "In Transit",
        shippingCompany: "Hapag Lloyd",
        commodity: "electronics",
        createdAt: new Date("2025-10-15"),
        updatedAt: new Date("2025-11-01"),
        waypoints: generateRealisticRoute("India, Mundra", "FOS, Francia", new Date("2025-10-15")),
        isConfirmed: false,
    },
    // ------------------- 2026 (fino a giugno) -------------------
    {
        id: "24",
        shipperId: "1",
        receiverId: "2",
        containerNumber: "TCLU1260001",
        route: { from: "Messico, Veracruz", to: "Civitavecchia, Italia" },
        progress: 60,
        eta: new Date("2026-01-18"),
        documentStatus: "Completo",
        riskLevel: "Low",
        value: { amount: 100000, currency: "USD" },
        status: "In Transit",
        shippingCompany: "MSC",
        commodity: "banana",
        createdAt: new Date("2025-12-20"),
        updatedAt: new Date("2026-01-05"),
        waypoints: generateRealisticRoute("Messico, Veracruz", "Civitavecchia, Italia", new Date("2025-12-20")),
        isConfirmed: true,
    },
    {
        id: "25",
        shipperId: "3",
        receiverId: "2",
        containerNumber: "IHDE1260002",
        route: { from: "Brasile, Santos", to: "Porto di Genova, Italia" },
        progress: 45,
        eta: new Date("2026-03-10"),
        documentStatus: "Pending",
        riskLevel: "Medium",
        value: { amount: 130000, currency: "USD" },
        status: "In Transit",
        shippingCompany: "CMA CGM",
        commodity: "sugar",
        createdAt: new Date("2026-02-01"),
        updatedAt: new Date("2026-02-20"),
        waypoints: generateRealisticRoute("Brasile, Santos", "Porto di Genova, Italia", new Date("2026-02-01")),
        isConfirmed: false,
    },
    {
        id: "26",
        shipperId: "1",
        receiverId: "2",
        containerNumber: "TCLU1260003",
        route: { from: "Tailandia, Laem Chabang", to: "Rotterdam, Paesi Bassi" },
        progress: 25,
        eta: new Date("2026-04-22"),
        documentStatus: "Pending",
        riskLevel: "High",
        value: { amount: 210000, currency: "USD" },
        status: "In Transit",
        shippingCompany: "Maersk",
        commodity: "electronics",
        createdAt: new Date("2026-03-10"),
        updatedAt: new Date("2026-03-25"),
        waypoints: generateRealisticRoute("Tailandia, Laem Chabang", "Rotterdam, Paesi Bassi", new Date("2026-03-10")),
        isConfirmed: true,
    },
    {
        id: "27",
        shipperId: "7",
        receiverId: "2",
        containerNumber: "SPCA1260004",
        route: { from: "Colombia, Cartagena", to: "Barcellona, Spagna" },
        progress: 70,
        eta: new Date("2026-05-30"), // fine maggio
        documentStatus: "Completo",
        riskLevel: "Low",
        value: { amount: 95000, currency: "USD" },
        status: "In Transit",
        shippingCompany: "ONE",
        commodity: "coffee",
        createdAt: new Date("2026-04-25"),
        updatedAt: new Date("2026-05-10"),
        waypoints: generateRealisticRoute("Colombia, Cartagena", "Barcellona, Spagna", new Date("2026-04-25")),
        isConfirmed: true,
    },
    {
        id: "28",
        shipperId: "1",
        receiverId: "2",
        containerNumber: "TCLU1260005",
        route: { from: "Ghana, Tema", to: "FOS, Francia" },
        progress: 15,
        eta: new Date("2026-06-18"), // giugno 2026
        documentStatus: "Pending",
        riskLevel: "Medium",
        value: { amount: 160000, currency: "USD" },
        status: "In Transit",
        shippingCompany: "Hapag Lloyd",
        commodity: "cocoa",
        createdAt: new Date("2026-05-20"),
        updatedAt: new Date("2026-06-01"),
        waypoints: generateRealisticRoute("Ghana, Tema", "FOS, Francia", new Date("2026-05-20")),
        isConfirmed: false,
    },
];

// In-memory storage - use predefined mock containers only (API provides real data)
let containers = [...mockContainers];

// Helper functions per CRUD operations
export const containerService = {
    // Get all containers
    getAll: (): Container[] => {
        return [...containers];
    },

    // Get container by ID
    getById: (id: string): Container | undefined => {
        return containers.find((container) => container.id === id);
    },

    // Get containers by shipper ID
    getByShipperId: (shipperId: string): Container[] => {
        return containers.filter((container) => container.shipperId === shipperId);
    },

    // Get containers by receiver ID
    getByReceiverId: (receiverId: string): Container[] => {
        return containers.filter((container) => container.receiverId === receiverId);
    },

    // Create new container
    create: (data: Omit<Container, "id" | "createdAt" | "updatedAt">): Container => {
        const newContainer: Container = {
            id: (containers.length + 1).toString(),
            ...data,
            createdAt: new Date(),
            updatedAt: new Date(),
        };
        containers.push(newContainer);
        return newContainer;
    },

    // Update container
    update: (id: string, data: Partial<Omit<Container, "id" | "createdAt" | "updatedAt">>): Container | null => {
        const index = containers.findIndex((container) => container.id === id);
        if (index === -1) return null;

        containers[index] = {
            ...containers[index],
            ...data,
            updatedAt: new Date(),
        };
        return containers[index];
    },

    // Delete container
    delete: (id: string): boolean => {
        const index = containers.findIndex((container) => container.id === id);
        if (index === -1) return false;

        containers.splice(index, 1);
        return true;
    },

    // Search and filter containers
    search: (filters: ContainerFilters): Container[] => {
        let filtered = [...containers];

        if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            filtered = filtered.filter(
                (container) =>
                    container.containerNumber?.toLowerCase().includes(searchTerm) ||
                    container.route.from.toLowerCase().includes(searchTerm) ||
                    container.route.to.toLowerCase().includes(searchTerm),
            );
        }

        if (filters.status) {
            filtered = filtered.filter((container) => container.status === filters.status);
        }

        if (filters.riskLevel) {
            filtered = filtered.filter((container) => container.riskLevel === filters.riskLevel);
        }

        if (filters.documentStatus) {
            filtered = filtered.filter((container) => container.documentStatus === filters.documentStatus);
        }

        if (filters.shipperId) {
            filtered = filtered.filter((container) => container.shipperId === filters.shipperId);
        }

        if (filters.receiverId) {
            filtered = filtered.filter((container) => container.receiverId === filters.receiverId);
        }

        if (filters.shippingCompany) {
            filtered = filtered.filter((container) => container.shippingCompany === filters.shippingCompany);
        }

        return filtered;
    },

    // Get statistics
    getStats: (shipperId?: string): ContainerStats => {
        let filteredContainers = shipperId ? containers.filter((c) => c.shipperId === shipperId) : containers;

        return {
            total: filteredContainers.length,
            inTransit: filteredContainers.filter((container) => container.status === "In Transit").length,
            delivered: filteredContainers.filter((container) => container.status === "Delivered").length,
            delayed: filteredContainers.filter((container) => container.status === "Delayed").length,
            docIssues: filteredContainers.filter((container) => container.documentStatus === "Pending").length,
            activeAlerts: 0, // No alerts in mock data
            highRisk: filteredContainers.filter((container) => container.riskLevel === "High" || container.riskLevel === "Critical").length,
        };
    },

    // Get recent containers
    getRecent: (limit: number = 5, shipperId?: string): Container[] => {
        let filteredContainers = shipperId ? containers.filter((c) => c.shipperId === shipperId) : containers;
        return [...filteredContainers].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()).slice(0, limit);
    },

    // Weekly calendar methods
    
    // Get containers grouped by week
    getContainersByWeek: (receiverId?: string, year: number = getCurrentYear()): Map<number, Container[]> => {
        let filteredContainers = receiverId ? containers.filter((c) => c.receiverId === receiverId) : containers;
        // Filter by year based on ETA
        filteredContainers = filteredContainers.filter((c) => c.eta?.getFullYear() === year);
        return groupContainersByWeek(filteredContainers);
    },

    // Get yearly week data for calendar view
    getYearlyWeekData: (receiverId?: string, year: number = getCurrentYear()): WeekData[] => {
        let filteredContainers = receiverId ? containers.filter((c) => c.receiverId === receiverId) : containers;
        // Filter by year based on ETA
        filteredContainers = filteredContainers.filter((c) => c.eta?.getFullYear() === year);
        return generateYearlyWeekData(filteredContainers, year);
    },

    // Get containers for a specific week
    getContainersForWeek: (weekNumber: number, receiverId?: string, year: number = getCurrentYear()): Container[] => {
        const weeklyData = containerService.getContainersByWeek(receiverId, year);
        return weeklyData.get(weekNumber) || [];
    },

    // Get week data for a specific week
    getWeekData: (weekNumber: number, receiverId?: string, year: number = getCurrentYear()): WeekData | null => {
        const yearlyData = containerService.getYearlyWeekData(receiverId, year);
        return yearlyData.find(week => week.weekNumber === weekNumber) || null;
    },

    // Create container from quote request
    createFromQuoteRequest: (quoteRequestId: string, orderId?: string): Container | null => {
        const quoteRequest = shipperQuoteService.getById(quoteRequestId);
        if (!quoteRequest) return null;

        const acceptedQuotes = quoteRequest.receivedQuotes.filter((qr) => qr.status === "accepted");
        if (acceptedQuotes.length === 0) return null;

        // Ottieni informazioni dall'ordine se disponibile
        let order = null;
        let receiverId = "";
        if (orderId) {
            order = orderService.getById(orderId);
            if (order) {
                receiverId = order.receiverId;
            }
        }

        // Se non abbiamo receiverId dall'ordine, prova a ottenerlo dall'account shipper
        if (!receiverId && quoteRequest.shipperId) {
            // Per ora usiamo un receiverId di default, in futuro potremmo avere una relazione
            // Per ora prendiamo il primo receiverId disponibile o un default
            receiverId = "2"; // Default receiver ID
        }

        // Aggrega informazioni dalle quote accettate
        // Prendi la prima quote per route base (le altre sono tratte intermedie)
        const primaryQuote = acceptedQuotes[0];
        
        // Estrai booking number dalla prima quote accettata che lo ha
        const quoteWithBookingNumber = acceptedQuotes.find(q => q.bookingNumber);
        const bookingNumber = quoteWithBookingNumber?.bookingNumber;
        
        // Lista di origini comuni dai container mock
        const commonOrigins = [
            "New York, USA", "Lima, Peru", "Nairobi, Kenya", "Santiago, Chile",
            "Guayaquil, Ecuador", "Costa Rica, San José", "Manila, Filippine",
            "Ghana, Accra", "Brasile, Santos", "Bangladesh, Chittagong",
            "Germania, Amburgo", "Cina, Shanghai", "Vietnam, Ho Chi Minh",
            "Colombia, Cartagena", "Thailandia, Bangkok", "India, Mumbai",
            "Brasile, Rio de Janeiro", "Messico, Veracruz", "USA, Miami",
            "Kenya, Mombasa", "Perù, Callao", "India, Mundra"
        ];
        
        // Lista di destinazioni comuni dai container mock
        const commonDestinations = [
            "Porto di Genova, Italia", "Rotterdam, Netherlands", "Porto di Livorno, Italia",
            "Barcellona, Spagna", "Civitavecchia, Italia", "FOS, Francia",
            "Rotterdam, Paesi Bassi"
        ];
        
        // Estrai route dalla quote, dalla quote request, o genera random
        let routeFrom = "Unknown";
        let routeTo = "Unknown";
        
        // Prova prima dalla quote accettata
        if (primaryQuote.extractedData?.origin) {
            routeFrom = primaryQuote.extractedData.origin;
        } else if (quoteRequest.extractedData?.origin) {
            routeFrom = quoteRequest.extractedData.origin;
        } else {
            // Genera random se non disponibile
            routeFrom = commonOrigins[Math.floor(Math.random() * commonOrigins.length)];
        }
        
        if (primaryQuote.extractedData?.destination) {
            routeTo = primaryQuote.extractedData.destination;
        } else if (quoteRequest.extractedData?.destination) {
            routeTo = quoteRequest.extractedData.destination;
        } else {
            // Genera random se non disponibile
            routeTo = commonDestinations[Math.floor(Math.random() * commonDestinations.length)];
        }

        // Calcola valore totale dalle quote accettate
        const totalValue = acceptedQuotes.reduce((sum, q) => sum + (q.price || 0), 0);
        const currency = primaryQuote.currency || "USD";

        // Determina commodity dall'extractedData
        let commodity: Container["commodity"] = "electronics";
        if (primaryQuote.extractedData?.product) {
            const product = primaryQuote.extractedData.product.toLowerCase();
            if (product.includes("avocado")) commodity = "avocado";
            else if (product.includes("blueberr") || product.includes("mirtillo")) commodity = "blueberries";
            else if (product.includes("banana") || product.includes("banana")) commodity = "banana";
            else if (product.includes("mango")) commodity = "mango";
            else if (product.includes("pineapple") || product.includes("ananas")) commodity = "pineapple";
            else if (product.includes("coffee") || product.includes("caffè")) commodity = "coffee";
            else if (product.includes("cocoa") || product.includes("cacao")) commodity = "cocoa";
            else if (product.includes("sugar") || product.includes("zucchero")) commodity = "sugar";
            else if (product.includes("textile") || product.includes("tessile")) commodity = "textiles";
            else if (product.includes("machinery") || product.includes("macchinari")) commodity = "machinery";
        }

        // Genera container number
        const shipperAccount = accountService.getById(quoteRequest.shipperId);
        const companyCode = shipperAccount?.legalName?.substring(0, 4).toUpperCase().replace(/\s/g, "") || "CONT";
        const containerNumber = `${companyCode}${Date.now().toString().slice(-6)}`;

        // Calcola ETA basandosi sulla delivery date della quote o sull'ordine
        let eta = new Date();
        if (primaryQuote.extractedData?.deliveryDate) {
            eta = primaryQuote.extractedData.deliveryDate;
        } else if (order) {
            const weekRange = getWeekRange(order.targetYear, order.targetWeek);
            eta = weekRange.end;
        } else {
            eta = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000); // 30 giorni da oggi
        }

        // Crea il container
        const newContainer: Container = {
            id: `container-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            shipperId: quoteRequest.shipperId,
            receiverId: receiverId,
            containerNumber: containerNumber,
            route: {
                from: routeFrom,
                to: routeTo
            },
            progress: 0,
            eta: eta,
            documentStatus: "Pending",
            riskLevel: "Low",
            value: {
                amount: totalValue,
                currency: currency
            },
            status: "In Transit",
            shippingCompany: undefined, // Potrebbe essere estratto dalle quote in futuro
            commodity: commodity,
            createdAt: new Date(),
            updatedAt: new Date(),
            waypoints: generateRealisticRoute(routeFrom, routeTo, new Date()),
            isConfirmed: true,
            orderId: orderId,
            quoteRequestId: quoteRequestId,
            bookingNumber: bookingNumber,
        };

        containers.push(newContainer);
        return newContainer;
    },
};
